<template>
  <div class="detail_courses">
    <div>
      <BlTextAv variation="caption-12" class="mb-8" color="subdued">Nama Pelatihan</BlTextAv>
      <div class="flex gap-3 detail_courses__border_bottom">
        <BlProductImageAv
          data-testid="course-image"
          :image-url="courseImg"
          image-placeholder="https://s2.bukalapak.com/attachment/237412/placeholder.png"
          object-fit="cover"
          class="rounded"
          size="extra-small"
        />
        <div>
          <BlTextAv variation="body-14" data-testid="course-name">{{ courseName }}</BlTextAv>
          <BlTextAv variation="caption-12" class="mt-4" color="subdued" data-testid="course-type">{{ courseType }}</BlTextAv>
        </div>
      </div>
      <div class="detail_courses__border_bottom mt-16">
        <BlTextAv variation="caption-12" color="subdued">Lembaga pelatihan</BlTextAv>
        <BlTextAv variation="body-14" class="mt-4" data-testid="course-merchant-name">{{ merchantName }}</BlTextAv>
      </div>
      <div class="detail_courses__border_bottom mt-16">
        <BlTextAv variation="caption-12" color="subdued">Waktu pelatihan</BlTextAv>
        <BlTextAv variation="body-14" class="mt-4" data-testid="course-time">{{ courseTime }}</BlTextAv>
      </div>
      <div v-if="courseIsLuring" class="detail_courses__border_bottom mt-16">
        <BlTextAv variation="caption-12" color="subdued">Tempat</BlTextAv>
        <BlTextAv variation="body-14" class="mt-4" data-testid="course-place">{{ coursePlace }}</BlTextAv>
      </div>

      <BlAccordionMv v-if="isEdukarierPage">
        <template #header>
          <div>
            <BlTextAv variation="caption-12" color="subdued">Total</BlTextAv>
            <BlTextAv variation="body-14" class="mt-4" data-testid="edukarier-header-total-invoice">{{ totalInvoice }}</BlTextAv>
          </div>
        </template>
        <template #content>
          <div class="flex justify-between">
            <BlTextAv variation="body-14">Harga kelas</BlTextAv>
            <BlTextAv variation="body-14" data-testid="edukarier-course-price">{{ courseEdukarierPrice }}</BlTextAv>
          </div>
          <div class="flex justify-between mv-8">
            <BlTextAv variation="body-14">Biaya layanan</BlTextAv>
            <BlTextAv variation="body-14" data-testid="edukarier-payment-fee">{{ paymentFee }}</BlTextAv>
          </div>
          <BlSeparatorAv />
          <div class="flex justify-between mt-8">
            <BlTextAv variation="body-14" weight="bold">Total</BlTextAv>
            <BlTextAv variation="body-14" weight="bold" data-testid="edukarier-total-invoice">{{ totalInvoice }}</BlTextAv>
          </div>
        </template>
      </BlAccordionMv>

      <div v-else class="mt-16">
        <BlTextAv variation="caption-12" color="subdued">Harga</BlTextAv>
        <BlTextAv variation="body-14" class="mt-4" data-testid="course-price">{{ coursePrice | money }}</BlTextAv>
        <BlTextAv variation="caption-12" class="mt-4" color="subdued">Dipotong dari saldo Kartu Prakerja</BlTextAv>
      </div>

      <BlSeparatorAv class="mt-8" />

      <div v-if="isEdukarierPage && paymentState !== 'pending'" class="mt-8">
        <BlTextAv variation="caption-12" color="subdued">Metode pembayaran</BlTextAv>
        <BlTextAv variation="body-14" class="mt-4" data-testid="payment-method">{{ paymentMethod }}</BlTextAv>
      </div>
    </div>
  </div>
</template>

<script>
import {BlProductImageAv, BlTextAv, BlAccordionMv, BlSeparatorAv} from '@bukalapak/bazaar-mweb-latest';
import {money} from '@bukalapak/toolbox-helper/number-helper';
import {formatPaymentType} from 'src/shared/javascripts/string-helpers';

export default {
  name: 'DetailCourse',
  components: {
    BlProductImageAv,
    BlTextAv,
    BlAccordionMv,
    BlSeparatorAv,
  },
  filters: {money},
  props: {
    transactionDetail: {
      type: Object,
      required: true,
    },
    invoiceDetail: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      courseTypes: {
        bauran_luring: 'Bauran Luring',
        bauran_daring: 'Bauran Daring',
        luring: 'Luring',
        video_conference: 'Video Conference',
        webinar: 'Webinar',
        self_paced: 'Pembelajaran Mandiri',
      },
    };
  },
  computed: {
    courseImg() {
      return this.transactionDetail.image.url;
    },
    courseName() {
      return this.transactionDetail.name;
    },
    merchantName() {
      return this.transactionDetail.merchant.name;
    },
    coursePlace() {
      if (!this.transactionDetail.course_detail) return '';

      const addressParts = [];
      if (this.transactionDetail.course_detail?.address) {
        addressParts.push(this.transactionDetail.course_detail.address);
      }
      if (this.transactionDetail.course_detail?.city) {
        addressParts.push(this.transactionDetail.course_detail?.city);
      }
      if (this.transactionDetail.course_detail?.province) {
        addressParts.push(this.transactionDetail.course_detail?.province);
      }
      return addressParts.join(', ');
    },
    coursePrice() {
      return this.transactionDetail.price;
    },
    courseType() {
      return this.courseTypes[this.transactionDetail?.course_detail?.course_type] || '-';
    },
    courseIsLuring() {
      return this.transactionDetail?.course_detail?.course_type === 'luring';
    },
    courseTime() {
      const times = this.transactionDetail?.course_detail?.times;
      if (!times || times.length === 0) return '-';

      const sortedTimes = times.sort(
        (a, b) => new Date(a.schedule_date + ' ' + a.start_time) - new Date(b.schedule_date + ' ' + b.start_time)
      );

      const firstTime = sortedTimes[0];
      const lastTime = sortedTimes[sortedTimes.length - 1];

      const startDate = new Date(firstTime.schedule_date);
      const endDate = new Date(lastTime.schedule_date);

      const options = {day: 'numeric', month: 'long'};
      const startDateString = startDate.toLocaleDateString('id-ID', options);
      const endDateString = endDate.toLocaleDateString('id-ID', options);

      const startTime = firstTime.start_time;
      const endTime = lastTime.end_time;
      const timezone = firstTime.timezone;

      return `${startDateString} - ${endDateString} (${startTime} - ${endTime} ${timezone})`;
    },
    totalInvoice() {
      return money(this.invoiceDetail?.amount?.total || 0);
    },
    courseEdukarierPrice() {
      return money(this.invoiceDetail?.amount?.details?.transactions || 0);
    },
    paymentFee() {
      return money(this.invoiceDetail?.amount?.details?.payment || 0);
    },
    isEdukarierPage() {
      const path = window.location.pathname;
      const segments = path.split('/');
      return segments?.[1] === 'edukarier';
    },
    paymentMethod() {
      return formatPaymentType(this.invoiceDetail?.payment_type);
    },
    paymentState() {
      return this.invoiceDetail?.state;
    },
  },
};
</script>

<style lang="scss" scoped>
.detail_courses {
  border: 1px solid #ebebeb;
  padding: 16px;
  border-radius: 12px;
  &__border_bottom {
    border-bottom: 1px solid #ebebeb;
    padding-bottom: 18px;
  }
  &::v-deep .bl-accordion__header {
    padding-left: 0px;
  }
  &::v-deep .bl-accordion__content-inner {
    padding-left: 0px;
  }
  &::v-deep .bl-accordion__item.is-accordion-active {
    border-bottom: 0px;
  }
}
</style>
